/**
 * Hook TanStack puro para busca de pedidos por fornecedor e loja.
 * Não depende de store ou UI. Retorna loading, erro e dados.
 *
 * @example
 * const { searchPedidos, isLoading, error, data, params } = useSearchPedidosTanstack();
 * searchPedidos('123', '01');
 */
"use client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { apiClient, URL } from "api";
import { usePrenotaStore } from "@PN/inclusao";
import { useCatalogs } from "store";
import type { Pedido, PedidoResponse } from "@apiInclui/pedido";

export interface UseSearchPedidosTanstackReturn {
  searchPedidos: (fornecedor: string, loja: string) => void;
  isLoading: boolean;
  error: Error | null;
  data: Pedido[] | null;
  params: { fornecedor: string; loja: string };
  clearCache: () => void;
}

export function useSearchPedidos_tanstack(): UseSearchPedidosTanstackReturn {
  const [params, setParams] = useState<{ fornecedor: string; loja: string }>({
    fornecedor: "",
    loja: "",
  });

  const queryClient = useQueryClient();
  const { draft } = usePrenotaStore();
  const { filiais } = useCatalogs();
  
  // Obtém a filial específica do draft (obtida do XML) ou fallback para filiais do usuário
  const filialAcesso = draft.filial;
  const filiaisUsuario = filiais.all?.map((f) => f.codigo) || [];

  // Usa filial específica se disponível, senão usa filiais do usuário
  const filiaisParaBusca = filialAcesso
    ? filialAcesso
    : filiaisUsuario.join(",");
  
  const enabled =
    !!params.fornecedor &&
    !!params.loja &&
    params.fornecedor.trim().length > 0 &&
    params.loja.trim().length > 0 &&
    (filiaisUsuario.length > 0);

  const {
    data,
    isFetching: isLoading,
    error,
  } = useQuery({
    queryKey: [
      "prenota-pedidos-com-produtos",
      params.fornecedor,
      params.loja,
      filialAcesso,
      filiaisUsuario.length,
    ],
    queryFn: async () => {
      if (!params.fornecedor.trim() || !params.loja.trim() || !filiaisParaBusca)
        return [];

      const { data } = await apiClient.internal.get<PedidoResponse>(
        URL.in.API_PRENOTAS_PEDIDOS,
        {
          headers: {
            fornecedor: params.fornecedor.trim(),
            loja: params.loja.trim(),
            filiais: filiaisParaBusca, // Usa filial específica ou fallback
          },
        }
      );
      if (!data?.success || !Array.isArray(data.data)) return [];
      return data.data;
    },
    enabled,
    staleTime: 10 * 1000,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: 1000,
  });

  const searchPedidos = useCallback((fornecedor: string, loja: string) => {
    setParams({ fornecedor, loja });
  }, []);

  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["prenota-pedidos-com-produtos"] });
    setParams({ fornecedor: "", loja: "" });
  }, [queryClient]);

  return {
    searchPedidos,
    isLoading,
    error: error ?? null,
    data: (data as Pedido[]) ?? null,
    params,
    clearCache,
  };
}
